import { FC, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import EventNoteIcon from '@mui/icons-material/EventNote';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import useInterval from 'use-interval';
import { api } from 'services/api';
import { getUserInfo } from 'redux/reducers/user/selectors';
import { getEMerchant } from 'redux/reducers/monthlyFee/selectors';
import { TEmCommentItem } from 'redux/reducers/emManagement/types';
import { TUser } from 'redux/reducers/user/types';
import { UserAvatar } from 'ui/UserAvatar';
import { ESortDirection } from 'ui/EasTable/types';
import { EasTooltip } from 'ui/EasTooltip';
import MenuItem from '@mui/material/MenuItem';
import { Divider, Menu } from '@mui/material';
import { INDICATOR_BADGE_SX } from '../constants';

const REFRESH_INTERVAL = 60_000;

const isScheduledForToday = (scheduledActionOn: string | null | undefined): boolean => {
	if (!scheduledActionOn) return false;
	return DateTime.fromISO(String(scheduledActionOn)).toLocal().hasSame(DateTime.local(), 'day');
};

export const NotificationsIndicator: FC = () => {
	const userinfo = useSelector(getUserInfo);
	const userId = userinfo?.id;
	const emList = useSelector(getEMerchant);

	const [notifications, setNotifications] = useState<TEmCommentItem[]>([]);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const emNames = useMemo(() => {
		const names: Record<number, string> = {};
		emList?.forEach((item) => {
			names[item.id] = item.fullName;
		});
		return names;
	}, [emList]);

	const fetchNotifications = useCallback(() => {
		if (!userId) return;
		api.emManagement
			.getEmNotifications({
				pageNumber: 1,
				pageSize: 100,
				orderBy: [{ fieldName: 'scheduledActionOn', direction: ESortDirection.ASC }],
				filters: [
					{ fieldName: 'scheduledActionOn', value: 'null', operator: '<>' },
					{ fieldName: 'scheduledActionOn', value: DateTime.utc().toISODate(), operator: '>=' },
				],
			})
			.then((response) => {
				const items = (response?.data ?? []).filter((item) =>
					item.operatorsToNotify?.some((operator) => operator.id === userId),
				);
				setNotifications(items);
			})
			.catch(() => {
				// The indicator is non-critical; keep the last known state on failure.
			});
	}, [userId]);

	useEffect(() => {
		fetchNotifications();
	}, [fetchNotifications]);

	useInterval(() => {
		fetchNotifications();
	}, REFRESH_INTERVAL);

	const hasUpcoming = notifications.length > 0;
	const todayCount = notifications.filter((item) =>
		isScheduledForToday(item.scheduledActionOn),
	).length;

	const handleOpen = (event: MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	const tooltipTitle =
		todayCount > 0
			? `${todayCount} notification${todayCount === 1 ? '' : 's'} scheduled for today`
			: hasUpcoming
				? 'You have upcoming notifications'
				: 'No upcoming notifications';

	return (
		<>
			<EasTooltip title={tooltipTitle}>
				<span>
					<Badge
						badgeContent={todayCount}
						color="error"
						invisible={todayCount === 0}
						sx={INDICATOR_BADGE_SX}
					>
						<IconButton
							color="primary"
							onClick={handleOpen}
							sx={{ p: 0.5 }}
							disabled={!hasUpcoming}
						>
							<EventNoteIcon sx={{ fontSize: '1.6rem' }} />
						</IconButton>
					</Badge>
				</span>
			</EasTooltip>
			<Menu
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<Box sx={{ p: 2, maxWidth: '900px', maxHeight: '600px', overflow: 'auto' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Upcoming Notifications
					</Typography>
					{notifications.map((item) => {
						const isToday = isScheduledForToday(item.scheduledActionOn);
						return (
							<Box key={item.id}>
								<MenuItem>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'start',
											alignItems: 'start',
											gap: 1,
											width: '100%',
										}}
									>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
												width: '100%',
												gap: 2,
											}}
										>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
												<Typography
													textAlign="left"
													sx={{ fontWeight: 400 }}
													color="text.secondary"
													variant="body2"
												>
													Scheduled action on:
												</Typography>
												<Typography
													textAlign="left"
													sx={{ fontWeight: isToday ? 900 : 400 }}
													color="text.secondary"
													variant="body2"
												>
													{new Date(item.scheduledActionOn ?? '').toLocaleDateString()}{' '}
												</Typography>
											</Box>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
												<Typography variant="body2" color="text.secondary">
													Notified to:
												</Typography>
												{item.operatorsToNotify.length > 0 &&
													item.operatorsToNotify.map((operator) => (
														<UserAvatar
															key={operator.id}
															user={operator as TUser}
															size="xxs"
															tooltip
														/>
													))}
											</Box>
										</Box>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
												gap: 1,
											}}
										>
											<Typography
												textAlign="left"
												sx={{ fontWeight: isToday ? 900 : 400 }}
												color="text.secondary"
												variant="body2"
												component={RouterLink}
												to={`/em-management/${item.emId}`}
												onClick={handleClose}
											>
												{emNames[item.emId]}
											</Typography>
										</Box>

										<Typography
											textAlign="left"
											sx={{
												fontWeight: isToday ? 700 : 400,
												maxWidth: '600px',
												whiteSpace: 'normal',
											}}
										>
											{item.comment}
										</Typography>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												gap: 1,
												width: '100%',
												justifyContent: 'space-between',
											}}
										>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
												<Typography variant="body2" color="text.secondary">
													Created at:
												</Typography>
												<Typography
													textAlign="left"
													sx={{ fontWeight: 400 }}
													color="text.secondary"
													variant="body2"
												>
													{new Date(item.createdAt).toLocaleDateString()}
												</Typography>
											</Box>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
												<Typography variant="body2" color="text.secondary">
													Created by:
												</Typography>
												<UserAvatar user={item.createdBy as TUser} size="xxs" tooltip />
											</Box>
										</Box>

										{/* <Typography
											textAlign="left"
											sx={{
												fontWeight: isToday ? 700 : 400,
												width: '600px',
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											}}
											variant="body2"
										>
											{item.createdBy.fullName}
										</Typography> */}
									</Box>
								</MenuItem>
								<Divider variant="middle" />
							</Box>
						);
					})}
				</Box>
			</Menu>
		</>
	);
};
